import _                    from 'underscore';
import React, { ReactNode } from 'react';
import { Checkbox }         from 'semantic-ui-react';

import { CorvalProject, CorvalProjectActivity, CorvalProjectSubObjective, CorvalStaticProjectActivity, SemanticUIColor, Uuid } from '../types';
import { usePrefStates }                                                                                                       from './usePrefStates';
import { useSystemModals }                                                                                                     from './useSystem';
import { BasicDialog }                                                                                                         from '../views/components/modals/BasicDialog';
import { BasicDialogContainer }                                                                                                from '../views/components/modals/BasicDialogContainer';
import { ACTIVITY_STATUS, ACTIVITY_STATUS_INFO, OBJECTIVE_STATUS }                                                             from '../constants/ProductEnums';
import { runIfFunction }                                                                                                       from '../util/FunctionalUtility';
import { percentage }                                                                                                          from '../util/data/DataMathUtility';
import { pluralize }                                                                                                           from 'apollo/lib/utils';
import { HarveyBallProps }                                                                                                     from '../views/components/util/visual/HarveyBall';
import { useProjectTermDictionary }                                                                                            from './useTermDictionary';
import { ProjectService, TimeService }                                                                                         from '../services';
import { ProjectTimelineDateFieldObject }                                                                                      from '../services/ProjectService';
import { useProjectActivities }                                                                                                from './useProjectActivities';
import { AddDeclinedProjectSubObjectivesDialog, AddDeclinedProjectSubObjectivesDialogProps }                                   from "@src/views/pages/projects/operations/AddDeclinedProjectSubObjectivesDialog";


export interface ObjectiveProgressionDetails {
    activities: ObjectiveProgressionDetailTotals,
    status: OBJECTIVE_STATUS
}

export interface ObjectiveProgressionDetailTotals {
    notStarted: number,
    inProgress: number,
    completed: number,
    willNotDo: number,
    total: number,
    totalActive: number,
}

export interface UseProjectObjectivesHook {

    generateObjectiveProgressStatus( projectActivities: Array<CorvalProjectActivity | CorvalStaticProjectActivity> ): ObjectiveProgressionDetails

    getObjectiveProgressionStatus( inProgress: number, complete: number, totalActivities: number ): OBJECTIVE_STATUS,

    getObjectiveCompletionPercentage( progress: ObjectiveProgressionDetails ): number,

    getObjectiveCompletionOutOfString( progress: ObjectiveProgressionDetails ): string,

    getObjectiveCompletionPercentageString( progress: ObjectiveProgressionDetails ): string,

    getObjectiveStatusCountStatement( progress: ObjectiveProgressionDetails, type: ACTIVITY_STATUS, includeCount?: boolean ): string,

    getObjectiveTotalCountStatement( progress: ObjectiveProgressionDetails, totalActive?: boolean, totalSuffix?: boolean ): string,

    getObjectiveStatusHarveyBallSectors( progress: ObjectiveProgressionDetails ): HarveyBallProps['sectors'],

    isObjectiveCompletelyDeferred( progress: ObjectiveProgressionDetails ): boolean,

    getObjectiveMilestoneOffsetLabel( offsetValue: number ): string,

    isMilestoneAligned( projectSubObjective: CorvalProjectSubObjective, project: CorvalProject | ProjectTimelineDateFieldObject[] ): boolean,

    getObjectiveTags( projectActivities: CorvalProjectActivity[] ): string[],

    getObjectiveHireTypes( projectActivities: CorvalProjectActivity[] ): string[],

    confirmToggleObjective: ConfirmationSetupMethod,
    confirmRevertObjectiveTiming: ConfirmationSetupMethod,

    addDeclinedProjectSubObjectives(
        projectId?: Uuid,
        projectVersionId?: Uuid,
        categoryId?: Uuid,
        subCategoryId?: Uuid,
        excludedTaxonomyIds?: Uuid[],
        mutationResultFields?: AddDeclinedProjectSubObjectivesDialogProps['mutationResultDataFields'],
        onComplete?: AddDeclinedProjectSubObjectivesDialogProps['onComplete']
    ): void,
}

type ConfirmationSetupMethod = ( onConfirm?: () => void, onCancel?: () => void, messageComponent?: ReactNode | null ) => void;

export const useProjectObjectives = ( projectId: Uuid ): UseProjectObjectivesHook => {

    let { getTerm }                     = useProjectTermDictionary(),
        [ , prefs, prefEnums ]          = usePrefStates( projectId ),
        modalActions                    = useSystemModals(),
        { getProjectActivityHireTypes } = useProjectActivities();

    let suppressRemovalWarningPrefKey   = prefEnums.OBJECTIVES_SUPPRESS_REMOVAL_WARNING,
        suppressReversionWarningPrefKey = prefEnums.OBJECTIVES_SUPPRESS_REVERSION_WARNING;

    let suppressRemovalWarning   = prefs.get( suppressRemovalWarningPrefKey ),
        suppressReversionWarning = prefs.get( suppressReversionWarningPrefKey );

    const addDeclinedProjectSubObjectives: UseProjectObjectivesHook['addDeclinedProjectSubObjectives'] = ( projectId, projectVersionId, categoryId, subCategoryId, excludedTaxonomyIds = [], mutationResultFields = null, onComplete = null, showImportOptions = false ) => {

        modalActions.show(
            <AddDeclinedProjectSubObjectivesDialog
                projectId={ projectId }
                projectVersionId={ projectVersionId }
                categoryId={ categoryId }
                subCategoryId={ subCategoryId }
                excludedTaxonomyIds={ excludedTaxonomyIds }
                mutationResultDataFields={ mutationResultFields }
                showImportOptions={ showImportOptions }
                onComplete={ ( data ) => {
                    modalActions.toggle( false );
                    runIfFunction( onComplete, data );
                } }
                successToastLabel={ 'Added Objectives & Activities Successfully!' }
            />, {
                // size: 'large'
            } );
    };

    const confirmToggleObjective: UseProjectObjectivesHook['confirmToggleObjective'] = ( onConfirm = null, onCancel = null, messageComponent = null ) => {

        if( !suppressRemovalWarning ){

            modalActions.show( (
                <BasicDialog
                    operationIsDangerous={ true }
                    submitLabel={ 'Proceed' }
                    className={ 'no-padding' }
                    onSubmit={ () => {
                        modalActions.toggle();
                        runIfFunction( onConfirm );
                    } }
                    onClose={ () => runIfFunction( onCancel ) }>

                    <BasicDialogContainer titleColor={ SemanticUIColor.RED } title={ 'Objective Update Notice' }>

                        { messageComponent ?? (
                            <>
                                <p>Updating your selection to remove this objective may cause certain activities tied to it to also be removed from the map. This action can lead to unexpected shifts in activity timing and map timing in general.</p>
                                <p>If you are sure that you and your team understand the changes that may occur, click proceed to continue.</p>
                            </>
                        ) }

                        <Checkbox
                            label={ 'Hide this warning in the future' }
                            className={ 'margin-md-top' }
                            onClick={ ( event, data ) => {
                                prefs.update( suppressRemovalWarningPrefKey, data.checked )
                            } }/>

                    </BasicDialogContainer>

                </BasicDialog>

            ), {
                size: 'tiny',
            } );

        } else {

            onConfirm();
        }
    }

    const confirmRevertObjectiveTiming: UseProjectObjectivesHook['confirmRevertObjectiveTiming'] = ( onConfirm = null, onCancel = null, messageComponent = null ) => {

        if( !suppressReversionWarning ){

            modalActions.show( (
                <BasicDialog
                    operationIsDangerous={ true }
                    submitLabel={ 'Proceed' }
                    className={ 'no-padding' }
                    onSubmit={ () => {
                        modalActions.toggle();
                        runIfFunction( onConfirm );
                    } }
                    onClose={ () => runIfFunction( onCancel ) }>

                    <BasicDialogContainer titleColor={ SemanticUIColor.RED } title={ 'Objective Update Notice' }>

                        { messageComponent ?? (
                            <>
                                <p>Reverting this objective will update the roadmap's timing, and activities from your map will realign their timing according to recommendations from Corval.</p>
                                <p>Are you sure you want to revert this objective's timing?</p>
                            </>
                        ) }

                        <Checkbox
                            label={ 'Hide this warning in the future' }
                            className={ 'margin-md-top' }
                            onClick={ ( event, data ) => {
                                prefs.update( suppressReversionWarningPrefKey, data.checked )
                            } }/>

                    </BasicDialogContainer>

                </BasicDialog>

            ), {
                size: 'tiny'
            } );

        } else {

            onConfirm();
        }
    }

    const generateObjectiveProgressStatus: UseProjectObjectivesHook['generateObjectiveProgressStatus'] = ( projectActivities: CorvalProjectActivity[] ): ObjectiveProgressionDetails => {

        // count each status
        let groupedStatuses = _.countBy( projectActivities, 'completionState' );

        // create progress object aggregates
        let objectiveProgress: ObjectiveProgressionDetails = {
            activities: {
                notStarted:  groupedStatuses[ ACTIVITY_STATUS.NOTSTARTED ] ?? 0,
                inProgress:  groupedStatuses[ ACTIVITY_STATUS.INCOMPLETE ] ?? 0,
                completed:   groupedStatuses[ ACTIVITY_STATUS.COMPLETE ] ?? 0,
                willNotDo:   groupedStatuses[ ACTIVITY_STATUS.EXCLUDED ] ?? 0,
                total:       projectActivities.length,
                totalActive: projectActivities.length - ( groupedStatuses[ ACTIVITY_STATUS.EXCLUDED ] ?? 0 ),
            },
            status:     null
        };

        // calculate status based on totals
        objectiveProgress.status = getObjectiveProgressionStatus(
            objectiveProgress.activities.inProgress,
            objectiveProgress.activities.completed,
            objectiveProgress.activities.totalActive
        );

        return objectiveProgress;
    }

    const isMilestoneAligned: UseProjectObjectivesHook['isMilestoneAligned'] = ( projectSubObjective, project ) => {

        const projectTimelineData = ( _.isArray( project ) )
                                    ? project
                                    : ProjectService.getTimelineDataObjectFromProjectFields( project as CorvalProject );

        const
            // User set milestones
            overrideMilestone    = projectSubObjective.projectTimingMilestoneOverride,
            overrideOffset       = projectSubObjective.projectTimingMilestoneOffsetMonthsOverride,
            hasOverrideMilestone = overrideMilestone !== null,
            // DEM set milestones
            milestone            = projectSubObjective.subObjective.projectTimingMilestone,
            milestoneOffset      = projectSubObjective.subObjective.projectTimingMilestoneOffsetMonths,
            hasMilestone         = milestone !== null,
            subObjectiveMoment   = TimeService.ISOtoMoment( projectSubObjective.startTimeUtc ),

            // Custom set milestones
            customMilestoneId    = projectSubObjective.projectCustomTimingMilestoneId;

        // Conditional check for if a user creates a completely custom milestone

        if( customMilestoneId ){
            const customMilestone        = _.findWhere( projectTimelineData, { fieldId: customMilestoneId } ),
                  projectMilestoneMoment = TimeService.ISOtoMoment( customMilestone.rawValue ).add( overrideOffset, 'months' );

            return TimeService.isDateUnitEqual( projectMilestoneMoment, subObjectiveMoment );
        }

        // Conditional check for if a user uses a templated, project defined milestone ( ie: Phase 2 Trial start date, etc )

        if( hasOverrideMilestone ){

            const projectMilestoneField  = ProjectService.getMilestoneInfoFromType( overrideMilestone ).PROJECT_FIELD,
                  projectMilestone       = _.findWhere( projectTimelineData, { fieldId: projectMilestoneField } ),
                  projectMilestoneMoment = TimeService.ISOtoMoment( projectMilestone.rawValue ).add( overrideOffset, 'months' );

            return TimeService.isDateUnitEqual( projectMilestoneMoment, subObjectiveMoment )

        }

        // Conditional check for if the DEM generated milestone

        if( hasMilestone ){

            const demMilestoneField  = ProjectService.getMilestoneInfoFromType( milestone ).PROJECT_FIELD,
                  demMilestone       = _.findWhere( projectTimelineData, { fieldId: demMilestoneField } ),
                  demMilestoneMoment = TimeService.ISOtoMoment( demMilestone.rawValue ).add( milestoneOffset, 'months' );

            return TimeService.isDateUnitEqual( demMilestoneMoment, subObjectiveMoment )
        }
        return true
    }

    const getObjectiveTags: UseProjectObjectivesHook['getObjectiveTags'] = ( projectActivities: CorvalProjectActivity[] ): string[] => {

        let allTags         = [],
            activityTagSets = projectActivities.map( pa => pa.activity.tags );

        // Aggregate all tags within activities under the project subobjective
        activityTagSets.map( activityTagSet => {

            if( activityTagSet.length ){
                activityTagSet.map( activityTag => {
                    allTags.push( activityTag.tagName );
                } )
            }
        } );

        // Reduce to unique entries
        allTags = _.uniq( allTags );

        return allTags;
    }

    // hireType info
    const getObjectiveHireTypes: UseProjectObjectivesHook['getObjectiveHireTypes'] = ( items ) => {

        let hireTypes = new Set()

        items.map( ( item ) => {

            getProjectActivityHireTypes( item ).map( ( item => hireTypes.add( item ) ) )

        } )
        return Array.from( hireTypes ) as string[];
    }

    const getObjectiveProgressionStatus: UseProjectObjectivesHook['getObjectiveProgressionStatus'] = ( inProgress, complete, totalActivities ) => {

        let totalStartedOrCompleted = inProgress + complete,
            totalPercentage         = percentage( inProgress + complete, totalActivities );

        if( totalStartedOrCompleted > 0 ){

            if( totalPercentage < 50 ){

                // Activities started are less than 50% of the total
                return OBJECTIVE_STATUS.IN_PROGRESS_LOW;

            } else if( totalPercentage >= 50 && totalPercentage < 75 ){

                // Activities started are at least 50%, but less than 75% of the total
                return OBJECTIVE_STATUS.IN_PROGRESS_MED;

            } else if( totalPercentage >= 75 && totalPercentage < 100 ){

                // Activities started are at least 75%, but less than 100% of the total, some activities may be in progress
                return OBJECTIVE_STATUS.IN_PROGRESS_HIGH;

            } else if( ( inProgress !== 0 ) && totalPercentage === 100 ){

                // Activities started account for all total (100%), but some are in progress
                return OBJECTIVE_STATUS.IN_PROGRESS_HIGH;

            } else if( ( inProgress === 0 ) && totalPercentage === 100 ){

                // Activities started account for all total (100%), and all are complete
                return OBJECTIVE_STATUS.COMPLETE;
            }
        }

        return OBJECTIVE_STATUS.NOT_STARTED;
    }

    const getObjectiveCompletionPercentage: UseProjectObjectivesHook['getObjectiveCompletionPercentage'] = ( progress ) => {

        if( !progress ) return 0;

        let { completed, totalActive } = progress.activities;

        return percentage( completed, totalActive, 'floor' );
    }

    const getObjectiveCompletionOutOfString: UseProjectObjectivesHook['getObjectiveCompletionOutOfString'] = ( progress ) => {

        if( !progress ) return '0 / 0';

        let { completed, totalActive } = progress.activities;

        return `${ completed } / ${ totalActive }`;
    }

    const getObjectiveCompletionPercentageString: UseProjectObjectivesHook['getObjectiveCompletionPercentageString'] = ( progress ) => {

        return `${ getObjectiveCompletionPercentage( progress ) }%`;
    }

    const getObjectiveStatusCountStatement: UseProjectObjectivesHook['getObjectiveStatusCountStatement'] = ( progress, type, withCount = true ) => {

        if( !progress ) return null;

        switch( type ){
            case ACTIVITY_STATUS.COMPLETE:
                return `${ withCount ? progress.activities.completed : '' } ${ progress.activities.completed != 1 ? 'Activities' : 'Activity' } ${ getTerm( ACTIVITY_STATUS_INFO.COMPLETE.LABEL ).toLowerCase() }`;
            case ACTIVITY_STATUS.INCOMPLETE:
                return `${ withCount ? progress.activities.inProgress : '' } ${ progress.activities.inProgress != 1 ? 'Activities' : 'Activity' } ${ getTerm( ACTIVITY_STATUS_INFO.INCOMPLETE.LABEL ).toLowerCase() }`;
            case ACTIVITY_STATUS.NOTSTARTED:
                return `${ withCount ? progress.activities.notStarted : '' } ${ progress.activities.notStarted != 1 ? 'Activities' : 'Activity' } ${ getTerm( ACTIVITY_STATUS_INFO.NOTSTARTED.LABEL ).toLowerCase() }`;
            case ACTIVITY_STATUS.EXCLUDED:
                return `${ withCount ? progress.activities.willNotDo : '' } ${ progress.activities.willNotDo != 1 ? 'Activities' : 'Activity' } ${ getTerm( ACTIVITY_STATUS_INFO.EXCLUDED.LABEL ).toLowerCase() }`;
        }
    }

    const getObjectiveTotalCountStatement: UseProjectObjectivesHook['getObjectiveTotalCountStatement'] = ( progress, totalActiveOnly = true, withSuffixString = true ) => {

        if( !progress ) return null;

        let statCount = totalActiveOnly ? progress.activities.totalActive : progress.activities.total;

        return `${ statCount } ${ statCount != 1 ? 'Activities' : 'Activity' } ${ withSuffixString ? 'Total' : '' }`;
    }

    const getObjectiveStatusHarveyBallSectors: UseProjectObjectivesHook['getObjectiveStatusHarveyBallSectors'] = ( progress ) => {

        if( !progress ) return 0;

        let sectorHash = {
            [ OBJECTIVE_STATUS.NOT_STARTED ]:      0,
            [ OBJECTIVE_STATUS.IN_PROGRESS_LOW ]:  1,
            [ OBJECTIVE_STATUS.IN_PROGRESS_MED ]:  2,
            [ OBJECTIVE_STATUS.IN_PROGRESS_HIGH ]: 3,
            [ OBJECTIVE_STATUS.COMPLETE ]:         4,
        }
        return sectorHash[ progress.status ] as HarveyBallProps['sectors'];
    }

    const isObjectiveCompletelyDeferred: UseProjectObjectivesHook['isObjectiveCompletelyDeferred'] = ( progress ) => {

        if( !progress ) return false;

        return ( progress.activities.willNotDo === progress.activities.total )
    }

    const getObjectiveMilestoneOffsetLabel: UseProjectObjectivesHook['getObjectiveMilestoneOffsetLabel'] = ( milestoneOffsetValue ) => {

        let useLabel  = ( milestoneOffsetValue && milestoneOffsetValue !== 0 ),
            indicator = ( milestoneOffsetValue > 0 ) ? '+' : '',
            units     = pluralize( milestoneOffsetValue, 'Months', 'Month' ),
            label     = `(${ indicator }${ units })`;

        return useLabel ? label : '';
    }

    return {
        addDeclinedProjectSubObjectives,
        confirmToggleObjective,
        confirmRevertObjectiveTiming,
        generateObjectiveProgressStatus,
        getObjectiveProgressionStatus,
        getObjectiveCompletionPercentage,
        getObjectiveCompletionOutOfString,
        getObjectiveStatusCountStatement,
        getObjectiveTotalCountStatement,
        getObjectiveCompletionPercentageString,
        getObjectiveStatusHarveyBallSectors,
        isObjectiveCompletelyDeferred,
        getObjectiveMilestoneOffsetLabel,
        getObjectiveTags,
        getObjectiveHireTypes,
        isMilestoneAligned
    } as UseProjectObjectivesHook;
};
